// F3/FR-SH5a §2 decision 9: pure body-builders for `PATCH /grackles/{id}/link`, factored out
// of ShareDialog so "what gets sent for each protection action" is unit-testable without
// rendering — this app has no jsdom/testing-library set up (see shareGateLogic.ts's test file for
// the same split elsewhere in this module, and clients/web/src/recorder/useRecorder.ts for the
// same convention predating F3).
//
// Every field here is optional-and-nullable on the wire (`shares.rs::PatchLinkBody`'s
// double-option idiom): an absent key means "no change" — `JSON.stringify` drops `undefined`
// keys for free — and `null` clears it. Callers pass the result straight to
// `api("PATCH", ..., body)`.

export interface PatchLinkRequest {
  password?: string | null;
  expires_at?: string | null;
  max_views?: number | null;
  require_email?: boolean;
  allowed_email_domains?: string[] | null;
}

export function parseDomainsInput(raw: string): string[] {
  return raw
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);
}

export function setPasswordBody(password: string): PatchLinkRequest {
  return { password };
}

export function clearPasswordBody(): PatchLinkRequest {
  return { password: null };
}

/** `localDateTimeValue` is an `<input type="datetime-local">` value (local time, no offset);
 * `new Date(...)` on that already treats it as local, so this only needs to re-encode to UTC
 * ISO-8601 for the wire. */
export function setExpiryBody(localDateTimeValue: string): PatchLinkRequest {
  return { expires_at: new Date(localDateTimeValue).toISOString() };
}

export function clearExpiryBody(): PatchLinkRequest {
  return { expires_at: null };
}

export function setMaxViewsBody(raw: string): PatchLinkRequest | { error: string } {
  const n = Number(raw);
  if (!raw || !Number.isInteger(n) || n < 1) {
    return { error: "Max views must be a whole number of at least 1." };
  }
  return { max_views: n };
}

export function clearMaxViewsBody(): PatchLinkRequest {
  return { max_views: null };
}

/** §2 F3 decision 9: clearing `require_email` must also clear the domain list in the same
 * request, or the PATCH 422s on the server's mirrored CHECK constraint
 * (`allowed_email_domains IS NULL OR (require_email AND cardinality >= 1)`). */
export function toggleRequireEmailBody(next: boolean, domainsInput: string): PatchLinkRequest {
  const domains = parseDomainsInput(domainsInput);
  return {
    require_email: next,
    allowed_email_domains: next && domains.length > 0 ? domains : null,
  };
}

export function saveDomainsBody(domainsInput: string): PatchLinkRequest {
  const domains = parseDomainsInput(domainsInput);
  return { allowed_email_domains: domains.length > 0 ? domains : null };
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in the viewer's local time; the server returns
 * (and expects) UTC ISO-8601 — the display-side inverse of `setExpiryBody`. */
export function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
